import React from "react"
import { Button } from "antd"
import { FileOutlined } from "@ant-design/icons"
import { PDFDocument, StandardFonts, rgb } from "pdf-lib"
import moment from "moment"

// Helper: safely convert a number to a fixed‑decimal string.
const safeToFixed = (value, decimals = 2) =>
  value != null && !isNaN(value) ? Number(value).toFixed(decimals) : "0.00"

const ReportPDFGenerator = ({
  reportData, // Array of invoice objects.
  reportTitle, // e.g., "Invoice Report"
  managerName, // e.g., "john.doe@example.com" or "All Users"
  dateRange, // Object: { from: "DD/MM/YYYY", to: "DD/MM/YYYY" }
  totals, // Object: { totalNetoSum, totalVat, totalSum }
}) => {
  const generatePDF = async () => {
    // Create a new PDF document.
    const pdfDoc = await PDFDocument.create()
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica)

    // Layout settings.
    const pageMargin = 50
    const headerFontSize = 16
    const infoFontSize = 12
    const textFontSize = 10
    const rowHeight = 20 // Increased for better spacing.
    const tableStartX = pageMargin
    // Define column widths: Invoice (80), Date (80), Client (200), Total (80).
    const colWidths = [80, 80, 200, 80]
    // Compute the x positions for each column.
    const colXPositions = []
    let currentX = tableStartX
    for (let width of colWidths) {
      colXPositions.push(currentX)
      currentX += width
    }
    const tableWidth = colWidths.reduce((sum, w) => sum + w, 0)
    const lineColor = rgb(0, 0, 0)

    // Start with a new page.
    let currentPage = pdfDoc.addPage()
    const { width, height } = currentPage.getSize()
    let yPosition = height - pageMargin

    // Draw the report header.
    currentPage.drawText(reportTitle, {
      x: pageMargin,
      y: yPosition,
      size: headerFontSize,
      font,
      color: lineColor,
    })
    yPosition -= headerFontSize + 5

    // Draw dynamic report info: selected manager and date range.
    const infoText = `Manager: ${managerName} | Date Range: ${dateRange.from} - ${dateRange.to}`
    currentPage.drawText(infoText, {
      x: pageMargin,
      y: yPosition,
      size: infoFontSize,
      font,
      color: lineColor,
    })
    yPosition -= infoFontSize + 15 // Extra space before table.

    // Draw table header with background and grid lines.
    currentPage.drawRectangle({
      x: tableStartX,
      y: yPosition - rowHeight,
      width: tableWidth,
      height: rowHeight,
      color: rgb(0.9, 0.9, 0.9),
    })
    const headers = ["Invoice", "Date", "Client", "Total"]
    for (let i = 0; i < headers.length; i++) {
      currentPage.drawText(headers[i], {
        x: colXPositions[i] + 5,
        y: yPosition - rowHeight + 5,
        size: textFontSize,
        font,
        color: lineColor,
      })
    }
    // Draw grid lines for header.
    currentPage.drawLine({
      start: { x: tableStartX, y: yPosition - rowHeight },
      end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
      thickness: 1,
      color: lineColor,
    })
    for (let i = 0; i < colXPositions.length; i++) {
      currentPage.drawLine({
        start: { x: colXPositions[i], y: yPosition },
        end: { x: colXPositions[i], y: yPosition - rowHeight },
        thickness: 1,
        color: lineColor,
      })
    }
    currentPage.drawLine({
      start: { x: tableStartX + tableWidth, y: yPosition },
      end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
      thickness: 1,
      color: lineColor,
    })
    yPosition -= rowHeight

    // Draw table rows.
    for (let i = 0; i < reportData.length; i++) {
      if (yPosition < pageMargin + rowHeight) {
        // Add a new page and redraw header.
        currentPage = pdfDoc.addPage()
        yPosition = currentPage.getSize().height - pageMargin
        // Redraw table header on new page.
        currentPage.drawRectangle({
          x: tableStartX,
          y: yPosition - rowHeight,
          width: tableWidth,
          height: rowHeight,
          color: rgb(0.9, 0.9, 0.9),
        })
        for (let j = 0; j < headers.length; j++) {
          currentPage.drawText(headers[j], {
            x: colXPositions[j] + 5,
            y: yPosition - rowHeight + 5,
            size: textFontSize,
            font,
            color: lineColor,
          })
        }
        currentPage.drawLine({
          start: { x: tableStartX, y: yPosition - rowHeight },
          end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
          thickness: 1,
          color: lineColor,
        })
        for (let j = 0; j < colXPositions.length; j++) {
          currentPage.drawLine({
            start: { x: colXPositions[j], y: yPosition },
            end: { x: colXPositions[j], y: yPosition - rowHeight },
            thickness: 1,
            color: lineColor,
          })
        }
        currentPage.drawLine({
          start: { x: tableStartX + tableWidth, y: yPosition },
          end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
          thickness: 1,
          color: lineColor,
        })
        yPosition -= rowHeight
      }

      const invoice = reportData[i]
      const docNumber = invoice.docNumber ? invoice.docNumber.toString() : ""
      const invoiceDate = invoice.invoiceDate
        ? moment(invoice.invoiceDate.seconds * 1000).format("DD/MM/YYYY")
        : ""
      const clientName = invoice.client && invoice.client.client ? invoice.client.client : ""
      const total =
        invoice.totalData && invoice.totalData.totalSum
          ? safeToFixed(invoice.totalData.totalSum)
          : "0.00"
      const rowValues = [docNumber, invoiceDate, clientName, total]

      // Optionally add an alternate row background.
      if (i % 2 === 1) {
        currentPage.drawRectangle({
          x: tableStartX,
          y: yPosition - rowHeight,
          width: tableWidth,
          height: rowHeight,
          color: rgb(0.95, 0.95, 0.95),
        })
      }
      // Draw row text.
      for (let j = 0; j < rowValues.length; j++) {
        currentPage.drawText(rowValues[j].toString(), {
          x: colXPositions[j] + 5,
          y: yPosition - rowHeight + 5,
          size: textFontSize,
          font,
          color: lineColor,
        })
      }
      // Draw grid lines for row.
      currentPage.drawLine({
        start: { x: tableStartX, y: yPosition - rowHeight },
        end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
        thickness: 0.5,
        color: lineColor,
      })
      for (let j = 0; j < colXPositions.length; j++) {
        currentPage.drawLine({
          start: { x: colXPositions[j], y: yPosition },
          end: { x: colXPositions[j], y: yPosition - rowHeight },
          thickness: 0.5,
          color: lineColor,
        })
      }
      currentPage.drawLine({
        start: { x: tableStartX + tableWidth, y: yPosition },
        end: { x: tableStartX + tableWidth, y: yPosition - rowHeight },
        thickness: 0.5,
        color: lineColor,
      })
      yPosition -= rowHeight
    }

    // Draw totals row.
    if (yPosition < pageMargin + rowHeight) {
      currentPage = pdfDoc.addPage()
      yPosition = currentPage.getSize().height - pageMargin
    }
    // Draw a line above the totals row.
    currentPage.drawLine({
      start: { x: tableStartX, y: yPosition },
      end: { x: tableStartX + tableWidth, y: yPosition },
      thickness: 1,
      color: lineColor,
    })
    const totalsText = `Totals: Neto: ${safeToFixed(totals.totalNetoSum)} | Vat: ${safeToFixed(
      totals.totalVat
    )} | Total: ${safeToFixed(totals.totalSum)}`
    currentPage.drawText(totalsText, {
      x: tableStartX + 5,
      y: yPosition - rowHeight + 5,
      size: textFontSize,
      font,
      color: lineColor,
    })
    // Draw border around totals row.
    currentPage.drawRectangle({
      x: tableStartX,
      y: yPosition - rowHeight,
      width: tableWidth,
      height: rowHeight,
      borderColor: lineColor,
      borderWidth: 1,
    })

    // Serialize the PDF and trigger a download.
    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "report.pdf"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Button onClick={generatePDF}>
      <FileOutlined /> Export to PDF
    </Button>
  )
}

export default ReportPDFGenerator
